// Library Methods
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import NavbarIconButton from "../NavbarIconButton/NavbarIconButton";
import { UserIcon } from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getPopoverPaperSx,
} from "src/styles/mui/muiStylesHelper";

type NavbarUserMenuType = {
  menuOptions: Array<{
    icon: JSX.Element;
    title: string;
    onClick: () => void;
    showOption: boolean;
  }>;
};

const NavbarUserMenu = ({ menuOptions }: NavbarUserMenuType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const [userMenu, setUserMenu] = useState<HTMLButtonElement | null>(null);

  const userMenuClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setUserMenu(event.currentTarget);

  // menu close handler
  const userMenuClose = () => setUserMenu(null);

  // close the menu on window resize
  useEffect(() => {
    const handleResize = () => userMenuClose();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* user menu button */}
      <NavbarIconButton
        dataTestId="navbar-account-button"
        title={t("Account")}
        onClick={userMenuClick}
        isUnderlined={false}
        icon={<UserIcon />}
      />

      {/* user menu, when open */}
      <Popover
        sx={{ mt: "16px" }}
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            width: window.innerWidth > 600 ? "280px" : "100%",
            padding: "8px",
            borderRadius: "4px",
            ...getPopoverPaperSx(theme),
          },
        }}
      >
        <Box display="flex" gap="4px" flexDirection="column" width="100%">
          {menuOptions.map((menuOption) => (
            <Button
              key={menuOption.title}
              onClick={() => {
                // close the user menu when an option is clicked
                menuOption.onClick();
                userMenuClose();
              }}
              startIcon={menuOption.icon}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                color: onSurface,
                textTransform: "capitalize",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#5B6D7A20",
                },
              }}
            >
              {menuOption.title}
            </Button>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default NavbarUserMenu;
